import { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useNavigate } from 'react-router-dom';
import { APIProvider } from '@vis.gl/react-google-maps';
import { PeopleDetail } from './components/people';
import { ErrorBoundary, AppLoader, hideInitialLoader, ToastContainer } from './components/ui';
// PlacesSyncDebug moved to UserLayout (admin footer)
// import PlacesSyncDebugPopup from './components/ui/PlacesSyncDebugPopup';
import { LanguageProvider, useLanguage } from './context/LanguageContext';
import { ThemeProvider, useTheme } from './context/ThemeContext';
import { AuthProvider, useAuth } from './context/AuthContext';
import { NotificationsProvider } from './context/NotificationsContext';
import { PreferencesProvider } from './context/PreferencesContext';
import { DomainProvider } from './context/DomainContext';
import { hotePeutEtreApp, libellesAppError } from './utils/domainBootstrap';
import { GlobalAudioProvider } from './context/GlobalAudioContext';
import { TutorialProvider } from './context/TutorialContext';
import { BetaProvider } from './context/BetaContext';
import SpeakingOverlay from './components/ui/SpeakingOverlay';
import TutorialOverlay from './components/tutorial/TutorialOverlay';
import NudgeManager from './components/help/NudgeManager';
import { getDomainConfig } from './services/domainService';

const GOOGLE_MAPS_API_KEY = import.meta.env.VITE_GOOGLE_MAPS_API_KEY;

// Mobile drag and drop polyfill for touch devices
import { polyfill } from 'mobile-drag-drop';
import 'mobile-drag-drop/default.css';

// Initialize touch drag and drop polyfill
polyfill({
    dragImageTranslateOverride: (dragImage, translate, evt) => {
        if (window.innerHeight - evt.clientY < 100) {
            window.scrollBy(0, 5);
        } else if (evt.clientY < 100) {
            window.scrollBy(0, -5);
        }
    }
});

// Auth Components
import { LoginPage, RegisterPage } from './components/auth';
import ResetPasswordPage from './components/auth/ResetPasswordPage';

// Public Components
import LandingPage from './components/public/LandingPage';
import DiscoverPage from './components/public/DiscoverPage';
import PricingPage from './components/public/PricingPage';
import PreviewPage from './components/public/PreviewPage';

// Legal Components
import LegalPage from './components/public/LegalPage';
import PrivacyPage from './components/public/PrivacyPage';
import TermsPage from './components/public/TermsPage';
import CookiesPage from './components/public/CookiesPage';

// Install App Page
import InstallAppPage from './components/public/InstallAppPage';

// Releases Page
import ContentPage from './components/public/ContentPage';

// Depot de photos par lien a usage limite (connecteur MCP)
import DepotPage from './components/public/DepotPage';

// Admin Test Pages
import MailTestPage from './components/admin/MailTestPage';

// Website Public Page
import { WebsitePublicPage } from './components/websites';

// Advertisement Public Page
import { AdvertisementPublicPage } from './components/advertisements';

// Payment Components
import PaymentCallback from './components/payment/PaymentCallback';

// Dashboard Components (logged-in users)
import UserDashboard from './components/dashboard/UserDashboard';
import { DashboardPeopleDetail, DashboardPeopleForm } from './components/people';
import { OccupationsPage } from './components/occupations';
import { EndroitsPage, EndroitDetail } from './components/places';
import { ContactDetail } from './components/contacts';

// Check if registration is disabled via environment variable
const isRegistrationDisabled = import.meta.env.VITE_DISABLE_REGISTRATION === 'true';

/**
 * AppRoutes - Main routing component that uses AuthContext
 */
function AppRoutes() {
    const navigate = useNavigate();
    const { language } = useLanguage();
    const { themeReady, loading: themeLoading } = useTheme();
    const { isAuthenticated, loginWithOAuth, isInitializing } = useAuth();

    // Check for OAuth callback parameters on mount
    useEffect(() => {
        const params = new URLSearchParams(window.location.search);
        const authToken = params.get('auth_token');
        const authEmail = params.get('auth_email');
        const authError = params.get('auth_error');

        if (authToken && authEmail) {
            loginWithOAuth(authToken, authEmail).then(result => {
                window.history.replaceState({}, document.title, window.location.pathname);
                if (result.success) {
                    alert(language === 'fr' ? 'Connexion réussie avec Google!' : 'Successfully logged in with Google!');
                } else {
                    alert(result.error || 'OAuth login failed');
                }
            });
        } else if (authError) {
            let errorMessage = language === 'fr' ? 'Erreur lors de la connexion: ' : 'Login error: ';
            switch (authError) {
                case 'email_not_verified':
                    errorMessage += language === 'fr' ? 'Email non vérifié' : 'Email not verified';
                    break;
                case 'token_exchange_failed':
                    errorMessage += language === 'fr' ? 'Échec de l\'échange de token' : 'Token exchange failed';
                    break;
                case 'userinfo_failed':
                    errorMessage += language === 'fr' ? 'Impossible de récupérer les informations utilisateur' : 'Failed to retrieve user information';
                    break;
                default:
                    errorMessage += authError;
            }
            alert(errorMessage);
            window.history.replaceState({}, document.title, window.location.pathname);
        }
    }, [language, loginWithOAuth]);

    // Hide the initial HTML loader when app is ready
    useEffect(() => {
        if (!isInitializing) {
            hideInitialLoader();
        }
    }, [isInitializing]);

    // Show loading screen while app is initializing or theme is loading (keeps HTML loader visible)
    if (isInitializing || !themeReady || themeLoading) {
        return <AppLoader />;
    }

    return (
        <>
            <Routes>
                {/* Public Routes */}
                {/* /discover n'est plus neutralisee : les mega-menus y
                    renvoient, et les pages de presentation des modules
                    existent desormais. */}
                <Route path="/discover" element={<DiscoverPage onLogin={() => navigate('/login')} />} />
                {/* La tarification n'est plus neutralisee pendant la beta :
                    les applications autonomes se vendent, et c'est la page
                    qui les presente. */}
                <Route path="/pricing" element={<PricingPage onLogin={() => navigate('/login')} />} />
                <Route path="/tarification" element={<PricingPage onLogin={() => navigate('/login')} />} />

                {/* Vitrine complete, telle qu'elle sera apres la beta.
                    Accessible meme pendant la beta, uniquement par son URL. */}
                <Route path="/preview" element={<PreviewPage onLogin={() => navigate('/login')} />} />
                <Route path="/payment/callback" element={<PaymentCallback onLogin={() => navigate('/login')} />} />

                {/* Legal Routes */}
                <Route path="/legal" element={<LegalPage />} />
                <Route path="/privacy" element={<PrivacyPage />} />
                <Route path="/terms" element={<TermsPage />} />
                <Route path="/cookies" element={<CookiesPage />} />

                {/* Install App Route */}
                <Route path="/install" element={<InstallAppPage />} />

                {/* Releases Route */}

                {/* Pages editoriales : le contenu vient de la base, redige
                    depuis l'administration. /p/:slug sert n'importe quelle
                    page, /module/:code celle qui decrit un module. */}
                <Route path="/p/:slug" element={<ContentPage resolveBy="slug" />} />
                <Route path="/module/:code" element={<ContentPage resolveBy="module" />} />

                {/* Depot de photos par lien a usage limite. Semi-publique :
                    le secret dans l'URL est la seule authentification, la page
                    s'ouvrant sur un telephone ou l'utilisateur n'est pas
                    forcement connecte. */}
                <Route path="/depot/:secret" element={<DepotPage />} />

                {/* Admin Test Pages */}
                <Route path="/admin/mail-test" element={<MailTestPage />} />

                {/* Website Public Page */}
                <Route path="/website/:uuid" element={<WebsitePublicPage />} />

                {/* Advertisement Public Page */}
                <Route path="/ad/:uuid" element={<AdvertisementPublicPage />} />

                <Route path="/login" element={
                    !isAuthenticated ? (
                        <LoginPage
                            onBack={() => window.history.back()}
                            language={language}
                            initialMode="login"
                        />
                    ) : <Navigate to="/" replace />
                } />
                <Route path="/register" element={
                    isRegistrationDisabled ? (
                        <Navigate to="/login" replace />
                    ) : !isAuthenticated ? (
                        <RegisterPage onLogin={() => navigate('/login')} />
                    ) : <Navigate to="/" replace />
                } />

                {/* Password Reset Route */}
                <Route path="/reset-password" element={<ResetPasswordPage language={language} />} />

                {/* Main Route Handling */}
                <Route
                    path="/"
                    element={
                        !isAuthenticated ? (
                            <LandingPage onLogin={() => navigate('/login')} />
                        ) : (
                            <UserDashboard />
                        )
                    }
                />

                <Route
                    path="/people/:uuid"
                    element={
                        isAuthenticated ? (
                            <DashboardPeopleDetail />
                        ) : (
                            <PeopleDetail />
                        )
                    }
                />

                {/* Private contact detail route - authenticated only */}
                <Route
                    path="/contact/:localuuid"
                    element={
                        isAuthenticated ? (
                            <ContactDetail />
                        ) : (
                            <Navigate to="/login" replace />
                        )
                    }
                />

                {/* Add contact route - authenticated only */}
                <Route
                    path="/add-contact"
                    element={
                        isAuthenticated ? (
                            <DashboardPeopleForm />
                        ) : (
                            <Navigate to="/login" replace />
                        )
                    }
                />

                {/* Occupations management - authenticated only */}
                <Route
                    path="/occupations"
                    element={
                        isAuthenticated ? (
                            <OccupationsPage />
                        ) : (
                            <Navigate to="/login" replace />
                        )
                    }
                />

                {/* Places management - authenticated only */}
                <Route
                    path="/endroits"
                    element={
                        isAuthenticated ? (
                            <EndroitsPage />
                        ) : (
                            <Navigate to="/login" replace />
                        )
                    }
                />

                {/* Place detail - authenticated only (supports both /endroits/:localUuid and legacy /endroit/:id) */}
                <Route
                    path="/endroits/:id"
                    element={
                        isAuthenticated ? (
                            <EndroitDetail />
                        ) : (
                            <Navigate to="/login" replace />
                        )
                    }
                />
                <Route
                    path="/endroit/:id"
                    element={
                        isAuthenticated ? (
                            <EndroitDetail />
                        ) : (
                            <Navigate to="/login" replace />
                        )
                    }
                />

                {/* Catch all - redirect to home */}
                <Route path="*" element={<Navigate to="/" replace />} />
            </Routes>

            {/* Debug moved to UserLayout (admin footer) */}
            <ToastContainer />
        </>
    );
}

function App() {
    const [domainConfig, setDomainConfig] = useState(null);
    const [domainError, setDomainError] = useState(false);

    useEffect(() => {
        const hostname = window.location.hostname;
        // Timeout safety: if API takes too long, render with fallback
        const timeout = setTimeout(() => {
            setDomainConfig(prev => {
                if (prev !== null) return prev;
                // Sur un hôte d'app, l'échec doit être BLOQUANT. Retomber sur
                // { found: false } afficherait la plateforme entière sous une
                // adresse d'application — exactement le repli permissif que
                // ce lot supprime ailleurs.
                if (hotePeutEtreApp(hostname)) setDomainError(true);
                return { found: false };
            });
        }, 3000);
        getDomainConfig(hostname)
            .then(config => {
                clearTimeout(timeout);
                setDomainConfig(config);
            })
            .catch(() => {
                clearTimeout(timeout);
                if (hotePeutEtreApp(hostname)) setDomainError(true);
                setDomainConfig({ found: false });
            });
        return () => clearTimeout(timeout);
    }, []);

    // Wait for domain config before rendering to avoid language flash
    if (domainConfig === null) {
        return null;
    }

    // Sur une adresse d'application, un échec de chargement est BLOQUANT.
    //
    // Se replier sur la plateforme complète montrerait vingt modules sous une
    // adresse qui n'en vend qu'un — le contraire de ce que le visiteur est
    // venu chercher, et une fuite du périmètre. Mieux vaut un écran franc.
    //
    // Sur la plateforme, ce cas n'existe pas : le repli y est légitime.
    //
    // L'écran est rendu AVANT LanguageProvider, donc hors de useTranslation.
    // Les traductions n'en dépendent pas pour autant : elles sont dans le
    // bundle, et la langue se lit du choix manuel ou du navigateur — ni l'un
    // ni l'autre ne passe par l'API qui vient d'échouer.
    if (domainError) {
        const t = libellesAppError();
        return (
            <div style={{
                minHeight: '100vh', display: 'flex', alignItems: 'center',
                justifyContent: 'center', padding: '24px', textAlign: 'center',
                fontFamily: 'system-ui, -apple-system, sans-serif', background: '#fff'
            }}>
                <div style={{ maxWidth: '420px' }}>
                    <h1 style={{ fontSize: '20px', marginBottom: '12px', color: '#111' }}>
                        {t.title}
                    </h1>
                    <p style={{ fontSize: '15px', lineHeight: 1.5, color: '#555', marginBottom: '20px' }}>
                        {t.message}
                    </p>
                    <button
                        onClick={() => window.location.reload()}
                        style={{
                            padding: '10px 20px', fontSize: '15px', cursor: 'pointer',
                            color: '#fff', border: 'none', borderRadius: '8px',
                            background: 'linear-gradient(135deg, #9333ea, #ec4899)'
                        }}
                    >
                        {t.retry}
                    </button>
                </div>
            </div>
        );
    }

    return (
        <ErrorBoundary>
            <APIProvider apiKey={GOOGLE_MAPS_API_KEY} libraries={['marker', 'places']}>
                {/* Au-dessus des préférences : le filtre du périmètre
                    s'applique avant l'écriture du cache des modules. */}
                <DomainProvider domainConfig={domainConfig} hasError={domainError}>
                <PreferencesProvider>
                    <LanguageProvider domainConfig={domainConfig}>
                        <ThemeProvider>
                            <GlobalAudioProvider>
                                <Router>
                                    <AuthProvider>
                                        <BetaProvider>
                                            <TutorialProvider>
                                                <NotificationsProvider>
                                                    <AppRoutes />
                                                    <SpeakingOverlay />
                                                    <TutorialOverlay />
                                                    <NudgeManager />
                                                </NotificationsProvider>
                                            </TutorialProvider>
                                        </BetaProvider>
                                    </AuthProvider>
                                </Router>
                            </GlobalAudioProvider>
                        </ThemeProvider>
                    </LanguageProvider>
                </PreferencesProvider>
                </DomainProvider>
            </APIProvider>
        </ErrorBoundary>
    );
}

export default App;
